if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        if (!this.form) {
          return;
        }

        this.refreshCartContext();

        if (this.variantIdInput) {
          this.variantIdInput.disabled = false;
        }
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.form.addEventListener('click', this.captureCartChangeAction.bind(this));
        this.form.addEventListener('pointerdown', this.captureCartChangeAction.bind(this));
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (this.cart) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      async onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.isSubmitting || this.submitButton.getAttribute('aria-disabled') === 'true') return;

        if (this.dataset.cartChange === 'true') {
          this.onCartChangeSubmit(evt);
          return;
        }

        // Lock before loading cart assets: two taps can otherwise pass the same guard.
        this.isSubmitting = true;
        this.submitButton.setAttribute('aria-disabled', 'true');
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner')?.classList.remove('hidden');

        try {
          await this.submitProduct(evt);
        } catch (error) {
          console.error(error);
          this.handleErrorMessage(window.cartStrings?.error);
        } finally {
          this.isSubmitting = false;
          this.submitButton.classList.remove('loading');
          if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
          if (!this.error) this.submitButton.removeAttribute('aria-disabled');
          this.querySelector('.loading__spinner')?.classList.add('hidden');
          CartPerformance.measureFromEvent('add:user-action', evt);
        }
      }

      async submitProduct(evt) {
        // Capture the accepted choice before any asynchronous asset loading.
        const formData = new FormData(this.form);
        const selectedVariantId = this.getSelectedVariantIdFromContext();
        if (selectedVariantId) formData.set('id', selectedVariantId);

        if (this.cartAddType === 'drawer' && typeof window.rpdLoadCartDrawerAssets === 'function') {
          try {
            await window.rpdLoadCartDrawerAssets();
          } catch (error) {
            console.warn('Cart drawer assets failed to load before add-to-cart.', error);
          }
        }
        if (this.cartAddType === 'notification' && typeof window.rpdLoadCartNotificationAssets === 'function') {
          try {
            await window.rpdLoadCartNotificationAssets();
          } catch (error) {
            console.warn('Cart notification assets failed to load before add-to-cart.', error);
          }
        }
        this.refreshCartContext();
        // A failed script can leave an unupgraded cart element in the DOM.
        // Use the normal cart-page fallback after a successful add in that case.
        if (this.cart && typeof this.cart.renderContents !== 'function') {
          this.cart = null;
          this.submitButton.removeAttribute('aria-haspopup');
        }

        const shouldPrimeSecondaryDrawer =
          this.cartAddType !== 'drawer' &&
          document.documentElement?.dataset?.cartType === 'drawer' &&
          typeof window.rpdLoadCartDrawerAssets === 'function';
        if (shouldPrimeSecondaryDrawer) {
          window.rpdLoadCartDrawerAssets().catch((error) => {
            console.warn('Cart drawer assets failed to prime for secondary cart sync.', error);
          });
        }

        this.handleErrorMessage();

        const atcEffect = this.dataset.atcEffect || this.cart?.dataset?.atcEffect;
        const confettiEffects = ['option_1', 'confetti_pop', 'confetti_fall'];
        const shouldConfetti = confettiEffects.includes(atcEffect);
        const shouldButtonSuccess = atcEffect === 'button_success';
        const shouldDelayCart = (shouldConfetti || shouldButtonSuccess) && this.cart?.tagName === 'CART-DRAWER';
        const atcDelay = shouldDelayCart ? 600 : 0;

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        if (this.cart) {
          const sections = this.getSectionsToRender();
          if (sections.length) {
            formData.append('sections', sections);
            formData.append('sections_url', window.location.pathname);
          }
          this.cart.setActiveElement?.(document.activeElement);
        }
        config.body = formData;

        return fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            const renderCart = () => {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
                this.renderSecondaryCart(response);
                if (this.cartAddType === 'drawer' && this.cart?.tagName !== 'CART-DRAWER') {
                  const drawer = this.cartDrawer;
                  if (drawer?.open && !drawer.classList.contains('active')) {
                    if (drawer.setRecoContext) drawer.setRecoContext('add');
                    drawer.open();
                  }
                }
              });
            };
            const renderWithDelay = () => {
              if (atcDelay > 0) {
                setTimeout(renderCart, atcDelay);
              } else {
                renderCart();
              }
            };
            if (shouldConfetti) {
              setTimeout(() => this.playAtcConfetti(atcEffect), 0);
            }
            if (shouldButtonSuccess) {
              setTimeout(() => this.showAtcButtonSuccess(), 0);
            }
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    renderWithDelay();
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              renderWithDelay();
            }
          });
      }

      async onCartChangeSubmit(evt) {
        const requestedAction = this.form.dataset.cartChangeAction || 'update';
        const actionButton =
          evt.submitter || this.form.querySelector('button[type="submit"]:focus') || this.submitButton;
        if (!actionButton || actionButton.getAttribute('aria-disabled') === 'true') return;
        if (this.form.dataset.cartChangeSubmitting === 'true') return;

        this.handleErrorMessage();

        const submitButtons = Array.from(this.form.querySelectorAll('button[type="submit"]'));
        this.form.dataset.cartChangeSubmitting = 'true';
        submitButtons.forEach((button) => {
          button.setAttribute('aria-disabled', 'true');
          if (button === actionButton) {
            button.classList.add('loading');
            button.querySelector('.loading__spinner')?.classList.remove('hidden');
          } else {
            button.classList.remove('loading');
            button.querySelector('.loading__spinner')?.classList.add('hidden');
          }
        });

        await this.waitForPendingVariantSelection();

        const formData = new FormData(this.form);
        const selectedVariantIdFromContext = this.getSelectedVariantIdFromContext();
        if (selectedVariantIdFromContext) {
          formData.set('id', selectedVariantIdFromContext);
          if (this.variantIdInput) {
            this.variantIdInput.value = selectedVariantIdFromContext;
            this.variantIdInput.disabled = false;
          }
        }
        const resolvedVariantId = await this.resolveSelectedVariantIdForCartChange(formData.get('id'));
        if (resolvedVariantId) {
          formData.set('id', resolvedVariantId);
          if (this.variantIdInput) {
            this.variantIdInput.value = resolvedVariantId;
            this.variantIdInput.disabled = false;
          }
        }
        const selectedVariantId = formData.get('id');
        const originalVariantId = this.dataset.cartChangeVariantId;
        const fallbackQty = parseInt(this.dataset.cartChangeQuantity || '1', 10);
        let desiredQty = parseInt(formData.get('quantity') || fallbackQty, 10);
        if (!desiredQty || Number.isNaN(desiredQty)) desiredQty = fallbackQty || 1;
        formData.set('quantity', desiredQty);
        const cartChangeAction =
          requestedAction === 'add' || actionButton.dataset.cartChangeAction === 'add' ? 'add' : 'update';

        const cartItems = this.getCartItemsForChange();
        const key = this.dataset.cartChangeKey;

        const finish = () => {
          this.form.dataset.cartChangeSubmitting = 'false';
          this.form.dataset.cartChangeAction = 'update';
          submitButtons.forEach((button) => {
            button.classList.remove('loading');
            button.querySelector('.loading__spinner')?.classList.add('hidden');
            if (!this.error) button.removeAttribute('aria-disabled');
          });
        };

        if (!selectedVariantId) {
          this.handleErrorMessage(window.cartStrings?.error || 'Error');
          this.error = true;
          finish();
          return;
        }

        if (cartChangeAction === 'add') {
          if (this.form.querySelector('input[name="quantity"]')?.type === 'hidden') {
            formData.set('quantity', 1);
          }
          this.requestCartAdd(cartItems, formData, selectedVariantId)
            .then(() => {
              if (!this.error) this.closeQuickAddModal();
            })
            .finally(() => finish());
          return;
        }

        if (String(selectedVariantId) === String(originalVariantId)) {
          if (cartItems) {
            this.requestCartChange(cartItems, { key, quantity: desiredQty, variantId: selectedVariantId })
              .then(() => {
                if (!this.error) this.closeQuickAddModal();
              })
              .catch((error) => this.handleCartChangeFailure(cartItems, error))
              .finally(() => finish());
            return;
          } else {
            window.location = window.routes.cart_url;
          }
          this.closeQuickAddModal();
          this.error = false;
          finish();
          return;
        }

        // Validate the original line before adding a replacement. Its key can
        // change when that add recalculates discounts, so retain its identity.
        let originalItem;
        let canMatchByIdentity = false;
        try {
          const cartState = await this.fetchCartState();
          originalItem = cartState.items.find(
            (item) => key && String(item.key) === String(key) && String(item.variant_id) === String(originalVariantId)
          );
          if (!originalItem) throw new Error('The original cart line has changed.');
          const identity = this.getCartLineIdentity(originalItem);
          canMatchByIdentity = cartState.items.filter((item) => this.getCartLineIdentity(item) === identity).length === 1;
        } catch (error) {
          await this.handleCartChangeFailure(cartItems, error);
          finish();
          return;
        }

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];
        config.body = formData;

        return fetch(`${routes.cart_add_url}`, config)
          .then(async (response) => {
            const result = await response.json();
            if (!response.ok && !result.status) throw new Error('Unable to add the replacement variant.');
            return result;
          })
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);
              this.error = true;
              return;
            }

            if (!cartItems) {
              window.location = window.routes.cart_url;
              return;
            }

            const addedItem = Array.isArray(response.items)
              ? response.items.find((item) => String(item.variant_id || item.id) === String(selectedVariantId))
              : response;
            if (String(addedItem?.variant_id || addedItem?.id) !== String(selectedVariantId)) {
              throw new Error('The replacement variant was not confirmed.');
            }

            return this.removeOriginalCartLine(cartItems, {
              key,
              originalItem,
              canMatchByIdentity,
              variantId: selectedVariantId,
            }).then(() => {
              if (!this.error) this.closeQuickAddModal();
            });
          })
          .catch((error) => this.handleCartChangeFailure(cartItems, error))
          .finally(() => {
            finish();
          });
      }

      async waitForPendingVariantSelection() {
        const productInfo = this.closest('product-info');
        if (!productInfo) return;
        if (productInfo.dataset.variantRequestPending !== 'true') return;

        await new Promise((resolve) => {
          let settled = false;
          const done = () => {
            if (settled) return;
            settled = true;
            clearTimeout(timeoutId);
            productInfo.removeEventListener('product-info:request-complete', done);
            resolve();
          };
          const timeoutId = window.setTimeout(done, 1200);
          productInfo.addEventListener('product-info:request-complete', done, { once: true });
        });
      }

      async resolveSelectedVariantIdForCartChange(fallbackVariantId) {
        try {
          const productInfo = this.closest('product-info');
          const variantSelectors = productInfo?.querySelector('variant-selects');
          if (!productInfo || !variantSelectors) {
            return fallbackVariantId ? String(fallbackVariantId) : null;
          }

          const selectedOptionValues = Array.from(
            variantSelectors.querySelectorAll('select option[selected], fieldset input:checked')
          )
            .map(({ dataset }) => dataset.optionValueId)
            .filter(Boolean);

          if (!selectedOptionValues.length) {
            return fallbackVariantId ? String(fallbackVariantId) : null;
          }

          const requestUrl = new URL(productInfo.dataset.url || window.location.pathname, window.location.origin);
          requestUrl.searchParams.set('section_id', productInfo.sectionId || productInfo.dataset.section || 'main');
          requestUrl.searchParams.set('option_values', selectedOptionValues.join(','));
          requestUrl.searchParams.delete('variant');

          const responseText = await fetch(requestUrl.toString()).then((response) => response.text());
          const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
          const selectedVariantScript = responseHTML.querySelector('variant-selects [data-selected-variant]');
          if (!selectedVariantScript) {
            return fallbackVariantId ? String(fallbackVariantId) : null;
          }

          const selectedVariant = JSON.parse(selectedVariantScript.textContent || '{}');
          if (!selectedVariant?.id) {
            return fallbackVariantId ? String(fallbackVariantId) : null;
          }

          return String(selectedVariant.id);
        } catch (error) {
          return fallbackVariantId ? String(fallbackVariantId) : null;
        }
      }

      captureCartChangeAction(event) {
        const submitButton = event.target?.closest?.('button[type="submit"]');
        if (!submitButton) return;
        const action = submitButton.dataset.cartChangeAction === 'add' ? 'add' : 'update';
        this.form.dataset.cartChangeAction = action;
      }

      async fetchCartState() {
        const response = await fetch(`${routes.cart_url}.js`, { cache: 'no-store' });
        if (!response.ok) throw new Error('Unable to read the current cart.');
        const cartState = await response.json();
        if (!Array.isArray(cartState?.items)) throw new Error('Invalid cart response.');
        return cartState;
      }

      getCartLineIdentity(item) {
        return JSON.stringify([
          String(item.variant_id),
          Object.entries(item.properties || {}).sort(([a], [b]) => a.localeCompare(b)),
          String(item.selling_plan_allocation?.selling_plan?.id || ''),
        ]);
      }

      async removeOriginalCartLine(cartItems, { key, originalItem, canMatchByIdentity = false, variantId }) {
        const cartState = await this.fetchCartState();
        const identity = originalItem && this.getCartLineIdentity(originalItem);
        const matches = identity
          ? cartState.items.filter((item) => this.getCartLineIdentity(item) === identity)
          : [];
        const original =
          matches.find((item) => key && String(item.key) === String(key)) ||
          (canMatchByIdentity && matches.length === 1 ? matches[0] : null);

        // A missing or ambiguous line must never fall back to its old position.
        if (!original?.key || original.quantity !== originalItem.quantity) {
          throw new Error('The original cart line cannot be identified safely.');
        }
        await this.requestCartChange(cartItems, { key: original.key, quantity: 0, variantId });
        if (this.error) throw new Error('Unable to remove the original cart line.');
      }

      async handleCartChangeFailure(cartItems, error) {
        console.error(error);
        this.error = true;
        const message = window.cartStrings?.error || 'Error';
        this.handleErrorMessage(message);

        // An add may have succeeded before the remove failed. Refresh all cart
        // sections so the customer can see the actual items and totals.
        const sectionIds = this.getCartSectionIds(cartItems);
        if (!sectionIds.length) return;
        try {
          const sectionUrl = new URL(routes.cart_url, window.location.origin);
          sectionUrl.searchParams.set('sections', sectionIds.join(','));
          const [cartState, response] = await Promise.all([this.fetchCartState(), fetch(sectionUrl)]);
          if (!response.ok) throw new Error('Unable to refresh the cart.');
          const sections = await response.json();
          if (sectionIds.some((id) => typeof sections[id] !== 'string')) {
            throw new Error('Incomplete cart sections.');
          }
          this.closeQuickAddModal();
          this.renderCartChange(cartItems, { ...cartState, sections });
          const errorId = this.dataset.cartChangeContext === 'drawer' ? 'CartDrawer-CartErrors' : 'cart-errors';
          let errorElement = document.getElementById(errorId);
          if (!errorElement || cartState.items.length === 0) {
            const errorRoot = this.dataset.cartChangeContext === 'drawer'
              ? document.querySelector('#CartDrawer .drawer__inner') || document.querySelector('cart-drawer-items')
              : document.querySelector('cart-items');
            errorElement = errorElement || document.createElement('div');
            errorElement.id = errorId;
            errorRoot?.append(errorElement);
          }
          if (errorElement) {
            errorElement.setAttribute('role', 'alert');
            errorElement.textContent = message;
          }
        } catch (refreshError) {
          console.error(refreshError);
        }
      }

      requestCartAdd(cartItems, formData, variantId) {
        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const sectionIds = this.getCartSectionIds(cartItems);
        if (sectionIds.length) {
          formData.append('sections', sectionIds);
          formData.append('sections_url', window.location.pathname);
        }
        config.body = formData;

        return fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'cart-change',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);
              this.error = true;
              return;
            }

            if (!cartItems) {
              window.location = window.routes.cart_url;
              return;
            }

            this.error = false;
            this.applyCartItemsUpdate(cartItems, response, sectionIds);
            this.publishCartChangeUpdate(response, variantId);
          });
      }

      requestCartChange(cartItems, { key, quantity, variantId }) {
        const sectionIds = this.getCartSectionIds(cartItems);
        const body = {
          quantity,
        };
        if (!key) return Promise.reject(new Error('A cart line key is required.'));
        body.id = String(key);
        if (sectionIds.length) {
          body.sections = sectionIds;
          body.sections_url = window.location.pathname;
        }

        return fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body: JSON.stringify(body) } })
          .then(async (response) => {
            const result = await response.json();
            if ((!response.ok || !Array.isArray(result.items)) && !result.errors) {
              throw new Error('Unable to confirm the cart change.');
            }
            return result;
          })
          .then((parsedState) => {
            if (parsedState.errors) {
              this.handleErrorMessage(parsedState.errors);
              this.error = true;
              return;
            }
            this.error = false;
            this.applyCartItemsUpdate(cartItems, parsedState, sectionIds);
            this.publishCartChangeUpdate(parsedState, variantId);
          });
      }

      renderCartChange(cartItems, parsedState) {
        if (!cartItems || !parsedState?.sections || !cartItems.getSectionsToRender) return;
        const cartDrawerWrapper = document.querySelector('cart-drawer');
        const cartFooter = document.getElementById('main-cart-footer');

        cartItems.classList.toggle('is-empty', parsedState.item_count === 0);
        if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
        if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

        cartItems.getSectionsToRender().forEach((section) => {
          const html = parsedState.sections[section.section];
          if (!html) return;
          const sectionMarkup = this.getSectionInnerHTML(html, section.selector);

          if (
            (section.id === 'cart-icon-bubble' || section.section === 'cart-icon-bubble') &&
            typeof window.updateCartIconBubbleCount === 'function'
          ) {
            window.updateCartIconBubbleCount(sectionMarkup);
            return;
          }

          const elementToReplace =
            document.getElementById(section.id)?.querySelector(section.selector) || document.getElementById(section.id);
          if (!elementToReplace) return;
          elementToReplace.innerHTML = sectionMarkup;
        });
      }

      getSectionInnerHTML(html, selector) {
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const element = parsed.querySelector(selector);
        return element ? element.innerHTML : '';
      }

      getCartItemsForChange() {
        const context = this.dataset.cartChangeContext;
        if (context === 'drawer') return document.querySelector('cart-drawer-items');
        if (context === 'page') return document.querySelector('cart-items');
        return document.querySelector('cart-items') || document.querySelector('cart-drawer-items');
      }

      closeQuickAddModal() {
        const modal = this.closest('quick-add-modal, cart-change-modal');
        if (modal) modal.hide(true);
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = this.dataset.cartChangeSubmitText || window.variantStrings.addToCart;
        }

        if (this.dataset.cartChange === 'true') {
          const addButton = this.form?.querySelector('button[data-cart-change-action="add"]');
          if (addButton) {
            if (disable) {
              addButton.setAttribute('disabled', 'disabled');
              addButton.setAttribute('aria-disabled', 'true');
            } else {
              addButton.removeAttribute('disabled');
              addButton.removeAttribute('aria-disabled');
            }
            const addLabel = addButton.querySelector('span');
            if (addLabel) {
              addLabel.textContent = this.dataset.cartChangeAddText || window.variantStrings.addToCart;
            }
          }
        }
      }

      getCartForAddType(type) {
        const fallback = document.querySelector('cart-notification') || this.cartDrawer;

        if (!type) return fallback;
        if (type === 'notification') return document.querySelector('cart-notification') || fallback;
        if (type === 'drawer') return this.cartDrawer || fallback;
        if (type === 'page') return null;
        return fallback;
      }

      getCartSectionIds(cartItems) {
        return cartItems?.getSectionsToRender
          ? cartItems
              .getSectionsToRender()
              .map((section) => section.section)
              .filter(Boolean)
          : [];
      }

      applyCartItemsUpdate(cartItems, parsedState, sectionIds) {
        if (sectionIds.length && parsedState?.sections) {
          this.renderCartChange(cartItems, parsedState);
        } else if (cartItems?.onCartUpdate) {
          cartItems.onCartUpdate();
        }
      }

      publishCartChangeUpdate(cartData, variantId) {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'cart-change',
          cartData,
          variantId,
        });
      }

      getSectionsToRender() {
        if (!this.cart || !this.cart.getSectionsToRender) return [];
        const sections = new Set();
        const addSections = (cartEl) => {
          if (!cartEl || !cartEl.getSectionsToRender) return;
          cartEl.getSectionsToRender().forEach((section) => sections.add(section.id));
        };

        addSections(this.cart);

        const cartType = document.documentElement?.dataset?.cartType;
        if (cartType === 'drawer' && this.cart?.tagName !== 'CART-DRAWER') {
          addSections(this.cartDrawer);
          sections.add('cart-drawer');
          sections.add('cart-icon-bubble');
        }

        return Array.from(sections);
      }

      renderSecondaryCart(parsedState) {
        const cartType = document.documentElement?.dataset?.cartType;
        if (cartType !== 'drawer' || this.cart?.tagName === 'CART-DRAWER') return;
        const hasDrawerSection = !!parsedState?.sections?.['cart-drawer'];

        const applyToDrawer = (drawerInstance) => {
          const resolvedDrawer = drawerInstance || this.cartDrawer;
          if (!resolvedDrawer?.renderContents) return false;
          if (!hasDrawerSection) {
            const drawerItems = resolvedDrawer.querySelector('cart-drawer-items');
            if (typeof drawerItems?.onCartUpdate === 'function') {
              drawerItems.onCartUpdate();
              return true;
            }
            return false;
          }
          resolvedDrawer.renderContents(parsedState, { open: false });
          return true;
        };

        if (applyToDrawer()) return;
        if (typeof window.rpdLoadCartDrawerAssets !== 'function') return;

        window.rpdLoadCartDrawerAssets()
          .then((drawerInstance) => {
            applyToDrawer(drawerInstance);
          })
          .catch((error) => {
            console.warn('Cart drawer assets failed to load for secondary cart sync.', error);
          });
      }

      refreshCartContext() {
        this.cartAddType =
          this.dataset.atcCartType ||
          document.documentElement?.dataset?.cartAddType ||
          document.documentElement?.dataset?.cartType;
        this.cart = this.getCartForAddType(this.cartAddType);

        if (this.submitButton) {
          if (this.cart) {
            this.submitButton.setAttribute('aria-haspopup', 'dialog');
          } else {
            this.submitButton.removeAttribute('aria-haspopup');
          }
        }
      }

      get cartDrawer() {
        return document.querySelector('cart-drawer') || window.cartDrawerElement || null;
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }

      getSelectedVariantIdFromContext() {
        const currentInputValue = this.variantIdInput?.value ? String(this.variantIdInput.value) : null;
        let selectedVariantId = null;
        try {
          const productInfo = this.closest('product-info');
          const selectedVariantScript = productInfo?.querySelector('variant-selects [data-selected-variant]');
          if (selectedVariantScript) {
            const selectedVariant = JSON.parse(selectedVariantScript.textContent || '{}');
            if (selectedVariant && selectedVariant.id) {
              selectedVariantId = String(selectedVariant.id);
            }
          }
        } catch (error) {
          selectedVariantId = null;
        }

        if (this.dataset.cartChange === 'true') {
          return selectedVariantId || currentInputValue || null;
        }

        return currentInputValue || selectedVariantId || null;
      }

      playAtcConfetti(effect = 'confetti_pop') {
        if (!this.submitButton) return;
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

        const host = this.submitButton.closest('.product-form__buttons') || this.submitButton;
        host.classList.add('atc-confetti-host');
        const existing = host.querySelector('.atc-confetti');
        if (existing) existing.remove();

        const container = document.createElement('span');
        const effectClass = effect === 'confetti_fall' ? 'atc-confetti--fall' : 'atc-confetti--pop';
        container.className = `atc-confetti ${effectClass}`;
        const pieces = 12;

        const colors = ['#f6c56f', '#f2a7a0', '#a6d7f5', '#c8e6d0'];
        for (let i = 0; i < pieces; i++) {
          const piece = document.createElement('span');
          piece.className = 'atc-confetti__piece';
          const x = Math.random() * 60 + 20;
          let dx = 0;
          let dy = 0;
          if (effect === 'confetti_fall') {
            dx = Math.random() * 18 - 9;
            dy = Math.random() * 28 + 24;
          } else {
            const angle = Math.random() * Math.PI * 2;
            const radius = Math.random() * 18 + 16;
            dx = Math.cos(angle) * radius;
            dy = Math.sin(angle) * radius;
          }
          const size = Math.random() * 3 + 4;
          const rot = Math.random() * 90 - 45;
          const rot2 = rot + (Math.random() * 220 - 110);
          piece.style.backgroundColor = colors[i % colors.length];
          piece.style.setProperty('--x', `${x}%`);
          piece.style.setProperty('--dx', `${dx}px`);
          piece.style.setProperty('--dy', `${dy}px`);
          piece.style.setProperty('--size', `${size}px`);
          piece.style.setProperty('--rot', `${rot}deg`);
          piece.style.setProperty('--rot2', `${rot2}deg`);
          piece.style.animationDelay = `${Math.random() * 0.05}s`;
          container.appendChild(piece);
        }

        host.appendChild(container);
        setTimeout(() => {
          container.remove();
          host.classList.remove('atc-confetti-host');
        }, 700);
      }

      showAtcButtonSuccess() {
        if (!this.submitButton || !this.submitButtonText) return;

        const successText = this.dataset.atcSuccessText || 'Added';
        if (!this.submitButton.dataset.atcOriginalText) {
          this.submitButton.dataset.atcOriginalText = this.submitButtonText.textContent;
        }

        this.querySelector('.loading__spinner')?.classList.add('hidden');
        this.submitButton.classList.remove('loading');
        this.submitButton.classList.add('atc-button-success');
        this.submitButtonText.textContent = successText;

        setTimeout(() => {
          this.submitButton.classList.remove('atc-button-success');
          if (this.submitButton.dataset.atcOriginalText) {
            this.submitButtonText.textContent = this.submitButton.dataset.atcOriginalText;
          }
        }, 900);
      }
    }
  );
}
